import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { IoAddOutline, IoCheckmark, IoChevronDown, IoPencilOutline, IoSyncOutline, IoTrashOutline, IoWarningOutline } from "react-icons/io5";
import {
  APP_ID_TO_WORKFLOW_TYPE,
  CATALOG_BY_WORKFLOW_TYPE,
  CONNECT_SOURCES_COMING_SOON,
  CONNECT_SOURCES_CRM,
  CONNECT_SOURCES_LIVE,
} from "./shared/connectSourcesCatalog";
import ConnectSourceCatalogIcon from "./shared/ConnectSourceCatalogIcon";
import { describeError } from "./shared/userFacingErrors";
import ConnectSourcesEmptyState from "./shared/ConnectSourcesEmptyState";
import ConnectorRequestModal from "./shared/ConnectorRequestModal";
import {
  deleteBusinessWorkflow,
  getBrowserTimezone,
  getBusinessWorkflows,
  getBusinessWorkflowStats,
  getGmailStatus,
  getWorkflowSyncStatus,
  syncWorkflowNow,
  updateBusinessWorkflow,
  updateGmailSettings,
} from "../businessModeService";
import { useActionItemsSidebarOptional } from "./ActionItemsSidebarContext";
import {
  buildReconnectSetupPath,
  mergeWorkflowIntoList,
  removeWorkflowFromList,
  resolveWorkflowConnectorId,
  resolveWorkflowSyncError,
  workflowNeedsReconnect,
} from "./shared/businessDigestSetup";
import {
  buildSettingsGmailFiltersPayload,
  MEETING_DEFAULT_KEYWORDS,
  MEETING_DEFAULT_SENDERS,
  parseFiltersPayload,
} from "./shared/businessFilterPayload";
import { getWorkflowLifecycleStatus, parseWorkflowTimestampMs } from "./shared/workflowStatus";
import GmailFilterSetupCard from "./shared/GmailFilterSetupCard";
import SettingsToggleRow from "./shared/SettingsToggleRow";
import CrmConnectorDetailPanel from "./shared/CrmConnectorDetailPanel";
import { getCrmSyncDisplayLabel } from "./shared/crmConnectorStatus";
import { InlineSpinner } from "./shared/SourceMessageAccordion";
import WorkflowSyncActivityPanel from "./shared/WorkflowSyncActivityPanel";
import NotionMeetingsPanel from "./shared/NotionMeetingsPanel";
import { formatSyncedStatusLabel } from "../../core/utils/timestampFormatter";
import { ActionItemsExpandableSearch } from "./shared/ActionItemsSearchToggle";
import { buildActionItemsListPath } from "./actionItemHelpers";

/** Popular connectors shown at the top of the catalog view. */
const POPULAR_CONNECTOR_IDS = ["granola-digest", "zoom-digest", "gong-digest"];

const CONNECTOR_FILTER_ALL = "all";
const CONNECTOR_FILTER_CONNECTED = "connected";
const CONNECTOR_FILTER_NOT_CONNECTED = "not-connected";

const CONNECTOR_FILTER_TABS = [
  { id: CONNECTOR_FILTER_ALL, label: "All" },
  { id: CONNECTOR_FILTER_CONNECTED, label: "Connected" },
  { id: CONNECTOR_FILTER_NOT_CONNECTED, label: "Not connected" },
];
const ADD_PICKER_GAP_PX = 6;
const ADD_PICKER_SEARCH_HEIGHT_PX = 46;
const ADD_PICKER_MIN_LIST_HEIGHT_PX = 96;
const ADD_PICKER_MAX_LIST_HEIGHT_PX = 360;
const ADD_PICKER_MAX_WIDTH_PX = 280;


/**
 * Turn a raw backend sync exception into short UI copy (never the raw text).
 *
 * @example
 * formatConnectorSyncError(
 *   "HTTPSConnectionPool(host='mcp.granola.ai', port=443): Read timed out. (read timeout=60)"
 * );
 * // "That took too long to respond. Try Sync now."
 */
const formatConnectorSyncError = (raw) =>
  describeError(raw, { context: "sync" }).message ||
  "Sync failed. Try Sync now — if it keeps happening, contact support.";

/**
 * Fixed coordinates for the add-connector menu so it is not clipped by
 * side-panel overflow containers.
 *
 * @example
 * resolveAddPickerPortalLayout(
 *   { top: 80, bottom: 108, right: 320, width: 72 },
 *   640,
 *   400
 * );
 * // { style: { position: "fixed", top: 114, right: 80 }, listMaxHeight: 240, openUp: false }
 */
const resolveAddPickerPortalLayout = (anchorRect, viewportWidth, viewportHeight) => {
  const spaceBelow = viewportHeight - anchorRect.bottom - ADD_PICKER_GAP_PX - 12;
  const spaceAbove = anchorRect.top - ADD_PICKER_GAP_PX - 12;
  const minPickerHeight = ADD_PICKER_SEARCH_HEIGHT_PX + ADD_PICKER_MIN_LIST_HEIGHT_PX;
  const openUp = spaceBelow < minPickerHeight && spaceAbove > spaceBelow;
  const available = openUp ? spaceAbove : spaceBelow;
  const listMaxHeight = Math.max(
    ADD_PICKER_MIN_LIST_HEIGHT_PX,
    Math.min(ADD_PICKER_MAX_LIST_HEIGHT_PX, available - ADD_PICKER_SEARCH_HEIGHT_PX)
  );

  const style = {
    position: "fixed",
    right: Math.max(8, viewportWidth - anchorRect.right),
    left: "auto",
    width: "max-content",
    minWidth: 220,
    maxWidth: Math.min(ADD_PICKER_MAX_WIDTH_PX, viewportWidth - 16),
  };

  if (openUp) {
    style.bottom = viewportHeight - anchorRect.top + ADD_PICKER_GAP_PX;
  } else {
    style.top = anchorRect.bottom + ADD_PICKER_GAP_PX;
  }

  return { style, listMaxHeight, openUp };
};

/**
 * Derive sidebar status dot state from a workflow object.
 *
 * @example
 * getSourceStatus({ active: true, last_synced_at: 1715000000 });
 * // { dot: "connected", label: "Connected" }
 *
 * @example
 * getSourceStatus({ active: false });
 * // { dot: "disabled", label: "Disabled" }
 */
const getSourceStatus = (workflow) => {
  if (!workflow) return { dot: "pending", label: "Setting up" };
  const status = getWorkflowLifecycleStatus(workflow);
  if (workflow.active === false) return { dot: "disabled", label: "Disabled" };
  if (workflowNeedsReconnect(workflow)) {
    return { dot: "error", label: "Reconnect required" };
  }
  const syncError = resolveWorkflowSyncError(workflow);
  if (syncError) {
    return { dot: "error", label: "Sync failed" };
  }
  // Cap the "syncing" indicator to the progress window so a stale RUNNING
  // job_status doesn't leave the dot blinking forever.
  if (status.showProgress && status.progressPercent < 100) {
    return { dot: "syncing", label: "Syncing" };
  }
  const lastSyncedMs = parseWorkflowTimestampMs(workflow.last_synced_at);
  if (lastSyncedMs > 0) return { dot: "connected", label: "Connected" };
  return { dot: "pending", label: "Setting up" };
};

const WORKFLOW_SYNC_IDLE = { state: "idle", error: null };

/** How often an open connector's counts refresh while it is still syncing. */
const SYNC_STATS_LIVE_REFRESH_MS = 8000;

/** Give up on a Sync now poll after this long, whatever phase the server reports. */
const SYNC_NOW_TOTAL_TIMEOUT_MS = 10 * 60 * 1000;
/** A transform phase that is not actually RUNNING after this long counts as finished. */
const SYNC_NOW_TRANSFORM_STALL_MS = 4 * 60 * 1000;

/**
 * Return in-session Sync now state for one workflow.
 *
 * @example
 * getWorkflowSyncNow({ "wf-1": { state: "failed", error: "token expired" } }, "wf-1");
 * // { state: "failed", error: "token expired" }
 */
const getWorkflowSyncNow = (syncNowByWorkflowId, workflowId) => {
  const id = String(workflowId || "").trim();
  if (!id) return WORKFLOW_SYNC_IDLE;
  return { ...WORKFLOW_SYNC_IDLE, ...(syncNowByWorkflowId[id] || {}) };
};

const isWorkflowSyncActive = (syncEntry) =>
  syncEntry?.state === "syncing" || syncEntry?.state === "processing";

/**
 * Human-readable sync status for catalog/sidebar rows.
 * Uses per-workflow Sync now state so Fetching/Processing survive Back.
 *
 * @param {object|null} workflow - Workflow record for the connector.
 * @param {{ syncNowByWorkflowId?: Record<string, { state?: string, error?: string|null }> }} options - Active sync context.
 * @returns {string} Display label such as "Synced …", "Fetching…", or "—".
 *
 * @example
 * getWorkflowSyncDisplayLabel({ last_synced_at: 1715000000000 }, {});
 * // "Synced Jun 26, 2026 1:12 PM"
 *
 * @example
 * getWorkflowSyncDisplayLabel(
 *   { workflow_id: "wf-1" },
 *   { syncNowByWorkflowId: { "wf-1": { state: "processing" } } }
 * );
 * // "Processing…"
 *
 * @example
 * getWorkflowSyncDisplayLabel(null, {});
 * // "—"
 */
/**
 * Build catalog table rows: one row per connected workflow (connector name), plus a
 * connect row for every live app (app name, e.g. "Zoom") so another account can be added.
 *
 * @example
 * buildCatalogTableRows({
 *   workflows: [{ workflow_id: "a", workflow_type: "zoom_daily_digest", name: "Sales Zoom" }],
 *   connectorFilter: "all",
 *   connectedTypeSet: new Set(["zoom_daily_digest"]),
 * });
 * // [
 * //   { kind: "connected", app: { appName: "Zoom", ... }, workflow: {...}, rowKey: "workflow:a" },
 * //   { kind: "placeholder", app: { appName: "Zoom", ... }, rowKey: "app:zoom-digest" },
 * //   { kind: "placeholder", app: { appName: "Granola", ... }, rowKey: "app:granola-digest" },
 * //   ...
 * // ]
 */
const buildCatalogTableRows = ({
  workflows = [],
  connectorFilter = CONNECTOR_FILTER_ALL,
  connectedTypeSet = new Set(),
  connectedCrmIds = new Set(),
}) => {
  const rows = [];

  for (const app of CONNECT_SOURCES_LIVE) {
    const workflowType = String(app.workflowType || "");
    const appWorkflows = workflows.filter(
      (workflow) => String(workflow.workflow_type || "") === workflowType
    );
    const hasConnection =
      appWorkflows.length > 0 || connectedTypeSet.has(workflowType);

    if (connectorFilter === CONNECTOR_FILTER_NOT_CONNECTED) {
      if (!hasConnection) {
        rows.push({ kind: "placeholder", app, rowKey: `app:${app.id}` });
      }
      continue;
    }

    if (connectorFilter === CONNECTOR_FILTER_CONNECTED) {
      for (const workflow of appWorkflows) {
        rows.push({
          kind: "connected",
          app,
          workflow,
          rowKey: `workflow:${String(workflow.workflow_id || "")}`,
        });
      }
      continue;
    }

    for (const workflow of appWorkflows) {
      rows.push({
        kind: "connected",
        app,
        workflow,
        rowKey: `workflow:${String(workflow.workflow_id || "")}`,
      });
    }
    rows.push({ kind: "placeholder", app, rowKey: `app:${app.id}` });
  }

  // Write-back CRM connectors (e.g. Attio) — OAuth credential, not a digest workflow.
  for (const app of CONNECT_SOURCES_CRM) {
    const crmId = String(app.id || "").trim();
    const isConnected = connectedCrmIds.has(crmId);
    if (connectorFilter === CONNECTOR_FILTER_CONNECTED && !isConnected) {
      continue;
    }
    if (connectorFilter === CONNECTOR_FILTER_NOT_CONNECTED && isConnected) {
      continue;
    }
    rows.push({
      kind: isConnected ? "crm-connected" : "oauth-connect",
      app,
      rowKey: `crm:${crmId}`,
    });
  }

  if (connectorFilter !== CONNECTOR_FILTER_CONNECTED) {
    for (const app of CONNECT_SOURCES_COMING_SOON) {
      rows.push({ kind: "coming-soon", app, rowKey: `soon:${app.id}` });
    }
  }

  return rows;
};

/**
 * Display label for a catalog table row (used for rendering and search).
 *
 * @example
 * getCatalogRowDisplayName({ kind: "connected", app: { appName: "Zoom" }, workflow: { name: "Sales Zoom" } });
 * // "Sales Zoom"
 */
const getCatalogRowDisplayName = (row) => {
  if (row.kind === "connected") {
    return String(row.workflow?.name || row.app?.appName || "").trim();
  }
  return String(row.app?.appName || "").trim();
};

const getWorkflowSyncDisplayLabel = (workflow, { syncNowByWorkflowId = {} } = {}) => {
  if (!workflow) return "—";
  const wId = String(workflow.workflow_id || "");
  const status = getSourceStatus(workflow);
  const lastSyncedMs = parseWorkflowTimestampMs(workflow.last_synced_at);
  const syncEntry = getWorkflowSyncNow(syncNowByWorkflowId, wId);
  if (isWorkflowSyncActive(syncEntry)) {
    return syncEntry.state === "processing" ? "Processing…" : "Fetching…";
  }
  if (status.dot === "syncing") return "Fetching…";
  // Error status must win over a stale last_synced_at — otherwise a failed
  // re-sync silently shows the earlier successful timestamp as if nothing
  // went wrong (the failure only ever surfaced via an in-session poll).
  if (status.dot === "error") return status.label;
  if (lastSyncedMs > 0) {
    return formatSyncedStatusLabel(workflow.last_synced_at) || status.label;
  }
  return status.label;
};

/**
 * True while a workflow is actively fetching or processing (manual Sync now or lifecycle).
 *
 * @example
 * isWorkflowSyncInProgress(
 *   { workflow_id: "wf-1", created_at: 1715000000, last_synced_at: 0 },
 *   {}
 * );
 * // true when first-run progress is still in the sync window
 */
const isWorkflowSyncInProgress = (workflow, { syncNowByWorkflowId = {} } = {}) => {
  if (!workflow) return false;
  const wId = String(workflow.workflow_id || "");
  const syncEntry = getWorkflowSyncNow(syncNowByWorkflowId, wId);
  if (isWorkflowSyncActive(syncEntry)) return true;
  return getSourceStatus(workflow).dot === "syncing";
};

/**
 * Sync column / header label with a spinning icon while fetch or transform is active.
 *
 * @example
 * <WorkflowSyncStatusLabel workflow={workflow} syncNowByWorkflowId={syncMap} />
 * // [spinning icon] Fetching…
 */
const WorkflowSyncStatusLabel = ({ workflow, syncNowByWorkflowId, className = "" }) => {
  const label = getWorkflowSyncDisplayLabel(workflow, { syncNowByWorkflowId });
  const inProgress = isWorkflowSyncInProgress(workflow, { syncNowByWorkflowId });
  return (
    <span
      className={`ds-sync-status-label${inProgress ? " ds-sync-status-label--active" : ""}${
        className ? ` ${className}` : ""
      }`}
    >
      {inProgress ? (
        <IoSyncOutline className="ds-sync-status-label__icon" size={12} aria-hidden="true" />
      ) : null}
      <span>{label}</span>
    </span>
  );
};

/**
 * Master-detail data sources panel inside Settings > Data Sources.
 *
 * Web variant: two-column sidebar + detail pane.
 * Extension variant: stacked list → detail (single column).
 *
 * @example
 * <DataSourcesView
 *   variant="web"
 *   workflows={workflows}
 *   connectedWorkflowTypes={connectedWorkflowTypes}
 *   onNavigateToSetup={(path) => navigate(path)}
 * />
 *
 * @example
 * <DataSourcesView
 *   variant="extension"
 *   workflows={workflows}
 *   onStartSetup={(workflowType, appName) => launchSetupPanel(workflowType, appName)}
 * />
 */
const DataSourcesView = forwardRef(function DataSourcesView(
  {
    variant = "web",
    workflows = [],
    workflowsLoading = false,
    connectedWorkflowTypes = [],
    connectedCrmIds = [],
    crmStatusById = {},
    onCrmStatusChange,
    focusAppId = "",
    focusWorkflowId = "",
    focusPending = false,
    onStartSetup,
    onNavigateToSetup,
    onWorkflowsChange,
    onDetailStateChange,
    onPasteTranscriptInstead,
    onGoToTasks,
  },
  ref
) {
  const isExtension = variant === "extension";
  const useCatalogLayout = !isExtension;
  const useStackedLayout = isExtension;
  const actionItemsSidebar = useActionItemsSidebarOptional();
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [selectedId, setSelectedId] = useState(null);
  const [selectedCrmId, setSelectedCrmId] = useState("");
  const [showDetail, setShowDetail] = useState(false);
  const [connectorFilter, setConnectorFilter] = useState(CONNECTOR_FILTER_ALL);
  const [catalogSearchQuery, setCatalogSearchQuery] = useState("");
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const [addPickerSearch, setAddPickerSearch] = useState("");
  const [connectorRequestTarget, setConnectorRequestTarget] = useState(null);

  const [formName, setFormName] = useState("");
  const [formSenderPatterns, setFormSenderPatterns] = useState(MEETING_DEFAULT_SENDERS);
  const [formSubjectKeywords, setFormSubjectKeywords] = useState(MEETING_DEFAULT_KEYWORDS);

  const [dirty, setDirty] = useState(false);
  const [gmailFiltersLoading, setGmailFiltersLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingWorkflowId, setDeletingWorkflowId] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [gmailFiltersOpen, setGmailFiltersOpen] = useState(false);
  const [syncStats, setSyncStats] = useState(null);
  const [syncStatsLoading, setSyncStatsLoading] = useState(false);
  /** Monotonic id so only the newest stats response is applied (a slow older one is dropped). */
  const syncStatsRequestRef = useRef(0);
  /** Per-workflow in-session Sync now state (state + error), keyed by workflow_id. */
  const [syncNowByWorkflowId, setSyncNowByWorkflowId] = useState({});
  /** workflowId -> sync error text the user dismissed (cleared on the next Sync now). */
  const [dismissedSyncErrors, setDismissedSyncErrors] = useState({});
  /** Prevents re-fetching detail when the workflows list refreshes in the background. */
  const detailLoadedForIdRef = useRef(null);
  /** Ignores stale Gmail filter responses when the user switches connectors quickly. */
  const detailLoadGenerationRef = useRef(0);
  const addPickerWrapRef = useRef(null);
  const addPickerRef = useRef(null);
  const [addPickerPortalLayout, setAddPickerPortalLayout] = useState(null);
  const syncNowPollRef = useRef(null);
  /** workflow_id owning the active Sync now poll interval. */
  const syncNowPollWorkflowIdRef = useRef("");
  const gmailAutoSaveTimerRef = useRef(null);
  const saveSuccessTimerRef = useRef(null);
  const saveInFlightRef = useRef(false);
  const saveAgainRef = useRef(false);
  const nameInputRef = useRef(null);
  const nameEditActiveRef = useRef(false);
  /** Latest workflows + callback so sync poll completion can patch last_synced_at. */
  const workflowsRef = useRef(workflows);
  const onWorkflowsChangeRef = useRef(onWorkflowsChange);
  /** Current selection — read from async delete/save so navigate-away stays correct. */
  const selectedIdRef = useRef(selectedId);
  /** Latest detail form values for auto-save without stale closures. */
  const formStateRef = useRef({
    formName: "",
    formSenderPatterns: MEETING_DEFAULT_SENDERS,
    formSubjectKeywords: MEETING_DEFAULT_KEYWORDS,
    dirty: false,
  });
  useEffect(() => {
    workflowsRef.current = workflows;
  }, [workflows]);
  useEffect(() => {
    onWorkflowsChangeRef.current = onWorkflowsChange;
  }, [onWorkflowsChange]);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);
  useEffect(() => {
    formStateRef.current = {
      formName,
      formSenderPatterns,
      formSubjectKeywords,
      dirty,
    };
  }, [formName, formSenderPatterns, formSubjectKeywords, dirty]);

  useEffect(() => {
    return () => {
      if (syncNowPollRef.current) clearInterval(syncNowPollRef.current);
      if (gmailAutoSaveTimerRef.current) clearTimeout(gmailAutoSaveTimerRef.current);
      if (saveSuccessTimerRef.current) clearTimeout(saveSuccessTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!editingName) return;
    nameInputRef.current?.focus();
    nameInputRef.current?.select();
  }, [editingName]);

  const selectedWorkflow = useMemo(
    () => workflows.find((w) => String(w.workflow_id || "") === selectedId) || null,
    [workflows, selectedId]
  );
  const selectedWorkflowId = String(selectedWorkflow?.workflow_id || "").trim();
  const selectedSyncNow = useMemo(
    () => getWorkflowSyncNow(syncNowByWorkflowId, selectedWorkflowId),
    [syncNowByWorkflowId, selectedWorkflowId]
  );
  const selectedSyncNowState = selectedSyncNow.state;
  const selectedSyncNowError = selectedSyncNow.error;

  /**
   * Merge in-session Sync now state for one workflow.
   *
   * @example
   * patchWorkflowSyncNow("wf-1", { state: "failed", error: "Sync failed" });
   */
  const patchWorkflowSyncNow = useCallback((workflowId, patch) => {
    const id = String(workflowId || "").trim();
    if (!id) return;
    setSyncNowByWorkflowId((previous) => {
      const current = { ...WORKFLOW_SYNC_IDLE, ...(previous[id] || {}) };
      const nextEntry = { ...current, ...patch };
      if (nextEntry.state === "idle" && !nextEntry.error) {
        const next = { ...previous };
        delete next[id];
        return next;
      }
      return { ...previous, [id]: nextEntry };
    });
  }, []);

  const selectedCrmApp = useMemo(() => {
    const crmId = String(selectedCrmId || "").trim();
    if (!crmId) return null;
    return CONNECT_SOURCES_CRM.find((app) => String(app.id || "") === crmId) || null;
  }, [selectedCrmId]);

  const selectedCrmStatus = useMemo(() => {
    const crmId = String(selectedCrmId || "").trim();
    if (!crmId) return null;
    return crmStatusById?.[crmId] || null;
  }, [crmStatusById, selectedCrmId]);

  const connectedTypeSet = useMemo(
    () =>
      new Set(
        (Array.isArray(connectedWorkflowTypes) ? connectedWorkflowTypes : []).map((t) =>
          String(t || "").trim()
        )
      ),
    [connectedWorkflowTypes]
  );

  const connectedCrmIdSet = useMemo(
    () =>
      new Set(
        (Array.isArray(connectedCrmIds) ? connectedCrmIds : []).map((id) =>
          String(id || "").trim()
        )
      ),
    [connectedCrmIds]
  );

  useImperativeHandle(ref, () => ({
    closeDetail: () => {
      setShowDetail(false);
      if (useCatalogLayout) {
        setSelectedId(null);
        setSelectedCrmId("");
      }
    },
  }), [useCatalogLayout]);

  useEffect(() => {
    if (!showDetail) {
      onDetailStateChange?.(false, "", "");
      return;
    }
    if (selectedCrmApp) {
      onDetailStateChange?.(
        true,
        String(selectedCrmApp.appName || "CRM"),
        ""
      );
      return;
    }
    if (!selectedWorkflow) {
      onDetailStateChange?.(false, "", "");
      return;
    }
    const catalog = CATALOG_BY_WORKFLOW_TYPE[String(selectedWorkflow.workflow_type || "")];
    onDetailStateChange?.(
      true,
      String(formName || selectedWorkflow.name || catalog?.appName || ""),
      String(selectedWorkflow.workflow_type || "")
    );
  }, [showDetail, selectedCrmApp, selectedWorkflow, formName, onDetailStateChange]);

  const loadDetail = useCallback(async (workflow) => {
    if (!workflow) return;
    const loadGeneration = ++detailLoadGenerationRef.current;
    if (gmailAutoSaveTimerRef.current) clearTimeout(gmailAutoSaveTimerRef.current);
    if (saveSuccessTimerRef.current) clearTimeout(saveSuccessTimerRef.current);
    setSaveError("");
    setSaveSuccess(false);
    setDirty(false);
    nameEditActiveRef.current = false;
    setEditingName(false);
    setNameDraft("");
    setGmailFiltersOpen(false);
    setSyncStats(null);
    setFormName(String(workflow.name || ""));

    const workflowId = String(workflow.workflow_id || "");
    if (workflowId) {
      const requestId = ++syncStatsRequestRef.current;
      setSyncStatsLoading(true);
      getBusinessWorkflowStats(workflowId)
        .then((stats) => {
          if (mountedRef.current && requestId === syncStatsRequestRef.current) setSyncStats(stats);
        })
        .catch(() => {})
        .finally(() => {
          if (mountedRef.current && requestId === syncStatsRequestRef.current) {
            setSyncStatsLoading(false);
          }
        });
    }

    const isEmailDigest = String(workflow.workflow_type || "") === "email_daily_digest";
    if (!isEmailDigest) {
      setGmailFiltersLoading(false);
      return;
    }

    setGmailFiltersLoading(true);
    try {
      const gmailStatus = await getGmailStatus();
      if (loadGeneration !== detailLoadGenerationRef.current || !mountedRef.current) {
        return;
      }
      const filterState = parseFiltersPayload(
        gmailStatus?.filters || workflow.filters || []
      );
      setFormSenderPatterns(
        String(filterState.meetingSenderPatterns || MEETING_DEFAULT_SENDERS)
      );
      setFormSubjectKeywords(
        String(filterState.meetingSubjectKeywords || MEETING_DEFAULT_KEYWORDS)
      );
    } catch (_) {
      // Leave defaults — save still works
    } finally {
      if (
        loadGeneration === detailLoadGenerationRef.current &&
        mountedRef.current
      ) {
        setGmailFiltersLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!selectedId || !selectedWorkflow) {
      detailLoadedForIdRef.current = null;
      return;
    }
    if (detailLoadedForIdRef.current === selectedId) {
      return;
    }
    detailLoadedForIdRef.current = selectedId;
    void loadDetail(selectedWorkflow);
  }, [selectedId, selectedWorkflow, loadDetail]);

  /**
   * Refetch the open connector's file/action counts without clearing what is on screen.
   * Without this the panel kept the numbers from when the detail opened (0 files, 0 actions
   * during a first sync) until the user left and came back.
   */
  const refreshSyncStatsQuietly = useCallback((workflowId) => {
    const id = String(workflowId || "").trim();
    if (!id) return;
    const requestId = ++syncStatsRequestRef.current;
    getBusinessWorkflowStats(id)
      .then((stats) => {
        if (mountedRef.current && requestId === syncStatsRequestRef.current) {
          setSyncStats(stats);
          setSyncStatsLoading(false);
        }
      })
      .catch(() => {});
  }, []);

  // The workflow row is polled while it syncs; any change to its sync fields (connector
  // finished, transform finished, first run completed) means the counts changed too.
  const selectedSyncSignature = selectedWorkflow
    ? [
        selectedWorkflow.last_synced_at,
        selectedWorkflow.job_status,
        selectedWorkflow.first_run_state,
        selectedWorkflow.first_run_in_progress,
      ].join("|")
    : "";
  const syncSignatureRef = useRef({ id: "", signature: "" });
  useEffect(() => {
    const previous = syncSignatureRef.current;
    syncSignatureRef.current = { id: selectedWorkflowId, signature: selectedSyncSignature };
    // Opening a connector is handled by loadDetail; only react to changes while it stays open.
    if (!selectedWorkflowId || previous.id !== selectedWorkflowId) return;
    if (previous.signature === selectedSyncSignature) return;
    refreshSyncStatsQuietly(selectedWorkflowId);
  }, [selectedWorkflowId, selectedSyncSignature, refreshSyncStatsQuietly]);

  // Counts also grow while the sync is still running, so keep them live until it settles.
  const selectedSyncInProgress = selectedWorkflow
    ? isWorkflowSyncInProgress(selectedWorkflow, { syncNowByWorkflowId })
    : false;
  useEffect(() => {
    if (!selectedWorkflowId || !selectedSyncInProgress) return undefined;
    const timerId = window.setInterval(
      () => refreshSyncStatsQuietly(selectedWorkflowId),
      SYNC_STATS_LIVE_REFRESH_MS
    );
    return () => window.clearInterval(timerId);
  }, [selectedWorkflowId, selectedSyncInProgress, refreshSyncStatsQuietly]);

  // Layout effects below: applying a deep-link focus must land before the first paint, or
  // the catalog flashes for a frame before the focused connector's detail replaces it.
  // A deep-link focus (settingsWorkflow / settingsApp) is applied ONCE per distinct value,
  // when its target first exists. These effects re-run on every `workflows` refresh (each
  // sync poll), so without the guard they dragged the user back into the focused connector
  // every few seconds and re-opened the detail view they had just closed.
  const appliedWorkflowFocusRef = useRef("");
  const appliedAppFocusRef = useRef("");

  useLayoutEffect(() => {
    const workflowId = String(focusWorkflowId || "").trim();
    if (!workflowId) {
      appliedWorkflowFocusRef.current = "";
      return;
    }
    if (appliedWorkflowFocusRef.current === workflowId) return;
    if (workflows.length === 0) {
      setSelectedId(null);
      return;
    }
    const match = workflows.find((w) => String(w.workflow_id || "") === workflowId);
    if (!match) {
      setSelectedId(null);
      return;
    }
    appliedWorkflowFocusRef.current = workflowId;
    setSelectedId(workflowId);
    if (useStackedLayout || useCatalogLayout) setShowDetail(true);
  }, [focusWorkflowId, workflows, useStackedLayout, useCatalogLayout]);

  useLayoutEffect(() => {
    if (String(focusWorkflowId || "").trim()) return;
    const appId = String(focusAppId || "").trim();
    if (!appId) {
      appliedAppFocusRef.current = "";
      return;
    }
    if (appliedAppFocusRef.current === appId) return;
    const workflowType = APP_ID_TO_WORKFLOW_TYPE[appId];
    if (workflowType) {
      if (workflows.length === 0) return;
      const match = workflows.find((w) => String(w.workflow_type || "") === workflowType);
      if (match) {
        appliedAppFocusRef.current = appId;
        setSelectedId(String(match.workflow_id || ""));
        if (useCatalogLayout) setShowDetail(true);
      }
      return;
    }
    if (connectedCrmIdSet.has(appId)) {
      appliedAppFocusRef.current = appId;
      setSelectedCrmId(appId);
      if (useCatalogLayout) setShowDetail(true);
    }
  }, [focusAppId, focusWorkflowId, workflows, useCatalogLayout, connectedCrmIdSet]);

  useEffect(() => {
    if (workflows.length === 0 || selectedId) return;

    const workflowId = String(focusWorkflowId || "").trim();
    if (workflowId) return;

    const appId = String(focusAppId || "").trim();
    if (appId) {
      const workflowType = APP_ID_TO_WORKFLOW_TYPE[appId];
      if (
        workflowType &&
        workflows.some((w) => String(w.workflow_type || "") === workflowType)
      ) {
        return;
      }
    }

    if (useCatalogLayout) return;

    const firstId = String(workflows[0]?.workflow_id || "").trim();
    if (firstId) {
      setSelectedId(firstId);
    }
  }, [workflows, selectedId, focusWorkflowId, focusAppId, useCatalogLayout]);

  useEffect(() => {
    if (!useStackedLayout && !useCatalogLayout) {
      setShowDetail(false);
    }
  }, [useStackedLayout, useCatalogLayout]);

  useEffect(() => {
    if (!addPickerOpen) {
      setAddPickerSearch("");
    }
  }, [addPickerOpen]);

  useEffect(() => {
    if (!addPickerOpen) return undefined;
    const handlePointerDown = (event) => {
      if (addPickerWrapRef.current?.contains(event.target)) return;
      if (addPickerRef.current?.contains(event.target)) return;
      setAddPickerOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [addPickerOpen]);

  const selectWorkflow = (workflowId) => {
    const nextId = String(workflowId || "").trim();
    if (nextId !== String(selectedId || "").trim()) {
      detailLoadedForIdRef.current = null;
      // Leaving a connector cancels an in-flight Sync now for a different workflow.
      const pollWorkflowId = String(syncNowPollWorkflowIdRef.current || "").trim();
      if (pollWorkflowId && nextId && nextId !== pollWorkflowId) {
        if (syncNowPollRef.current) clearInterval(syncNowPollRef.current);
        syncNowPollWorkflowIdRef.current = "";
        patchWorkflowSyncNow(pollWorkflowId, { state: "idle", error: null });
      }
    }
    setSelectedCrmId("");
    setSelectedId(nextId);
    setAddPickerOpen(false);
    if (useStackedLayout || useCatalogLayout) setShowDetail(true);
  };

  const selectCrmConnector = (crmId) => {
    const nextCrmId = String(crmId || "").trim();
    if (!nextCrmId) return;
    setSelectedId(null);
    detailLoadedForIdRef.current = null;
    setSelectedCrmId(nextCrmId);
    setAddPickerOpen(false);
    if (useStackedLayout || useCatalogLayout) setShowDetail(true);
  };

  /**
   * Reload workflows after Sync now so catalog Sync labels pick up last_synced_at.
   *
   * @param {string} workflowId - Workflow that finished syncing (used to return that row).
   * @returns {Promise<object|null>} Matching workflow from the refreshed list, or null.
   *
   * @example
   * await refreshWorkflowsAfterSync("wf-1");
   * // { workflow_id: "wf-1", last_synced_at: "2026-07-08T…" }
   */
  const refreshWorkflowsAfterSync = useCallback(async (workflowId) => {
    const id = String(workflowId || "").trim();
    try {
      const workflowsData = await getBusinessWorkflows();
      if (!mountedRef.current) return null;
      const next = Array.isArray(workflowsData) ? workflowsData : [];
      const onChange = onWorkflowsChangeRef.current;
      if (typeof onChange === "function") {
        onChange(next);
      }
      if (!id) return null;
      return next.find((workflow) => String(workflow?.workflow_id || "").trim() === id) || null;
    } catch (_) {
      return null;
    }
  }, []);

  const handleSyncNow = async () => {
    if (!selectedWorkflow || isWorkflowSyncActive(selectedSyncNow)) return;
    const workflowId = String(selectedWorkflow.workflow_id || "");
    if (!workflowId) return;
    setDismissedSyncErrors((prev) => {
      if (!(workflowId in prev)) return prev;
      const next = { ...prev };
      delete next[workflowId];
      return next;
    });
    patchWorkflowSyncNow(workflowId, { state: "syncing", error: null });
    syncNowPollWorkflowIdRef.current = workflowId;
    if (syncNowPollRef.current) clearInterval(syncNowPollRef.current);

    let connectorJobId = null;
    let queuedAtMs = 0;
    let transformJobId = null;
    try {
      const result = await syncWorkflowNow(workflowId);
      connectorJobId = result?.connector_job_id || null;
      queuedAtMs = result?.queued_at_ms || 0;
      transformJobId = result?.transform_job_id || null;
      // already_syncing during transform phase: reflect it in the UI immediately
      if (result?.status === "already_syncing" && result?.phase === "transform") {
        patchWorkflowSyncNow(workflowId, { state: "processing", error: null });
      }
    } catch (e) {
      patchWorkflowSyncNow(workflowId, {
        state: "failed",
        error: e?.message || "Failed to start sync",
      });
      syncNowPollWorkflowIdRef.current = "";
      return;
    }

    const deadline = Date.now() + SYNC_NOW_TOTAL_TIMEOUT_MS;
    // The server reports phase "transform" both while a transform really runs and while it
    // only knows one is *scheduled* (or none exists, e.g. the sync found nothing new). Only
    // the former is progress, so a transform that never reaches RUNNING is not waited on.
    let transformPhaseSinceMs = 0;

    /** Stop polling, clear the spinner, and pull the fresh workflow row + detail. */
    const settleSync = async () => {
      if (syncNowPollRef.current) clearInterval(syncNowPollRef.current);
      syncNowPollWorkflowIdRef.current = "";
      patchWorkflowSyncNow(workflowId, { state: "idle", error: null });
      const fresh = await refreshWorkflowsAfterSync(workflowId);
      if (mountedRef.current && fresh) {
        void loadDetail(fresh);
      } else if (mountedRef.current) {
        const fallback =
          workflowsRef.current.find(
            (workflow) => String(workflow?.workflow_id || "").trim() === workflowId
          ) || null;
        if (fallback) void loadDetail(fallback);
      }
    };

    syncNowPollRef.current = setInterval(async () => {
      try {
        const result = await getWorkflowSyncStatus(workflowId, {
          connectorJobId: connectorJobId || undefined,
          sinceMs: queuedAtMs || undefined,
          transformJobId: transformJobId || undefined,
        });
        const status = String(result?.status || "").toUpperCase();
        const phase = String(result?.phase || "");
        const nowMs = Date.now();
        if (status === "DONE") {
          await settleSync();
        } else if (status === "FAILED") {
          clearInterval(syncNowPollRef.current);
          syncNowPollWorkflowIdRef.current = "";
          patchWorkflowSyncNow(workflowId, {
            state: "failed",
            error: result?.error || "Sync failed",
          });
        } else if (nowMs > deadline) {
          // Checked in every phase: a poll stuck in "transform" used to skip this
          // branch and spin forever.
          await settleSync();
        } else if (phase === "transform") {
          if (!transformPhaseSinceMs) transformPhaseSinceMs = nowMs;
          const transformRunning =
            String(result?.transform?.status || "").toUpperCase() === "RUNNING";
          if (!transformRunning && nowMs - transformPhaseSinceMs > SYNC_NOW_TRANSFORM_STALL_MS) {
            await settleSync();
          } else {
            patchWorkflowSyncNow(workflowId, { state: "processing", error: null });
          }
        }
      } catch (e) {
        const message = String(e?.message || "Sync failed");
        // Transient CORS/network blips (e.g. Flask reload) must not mark the
        // connector sync itself as failed.
        if (message === "Failed to fetch" && Date.now() <= deadline) {
          return;
        }
        clearInterval(syncNowPollRef.current);
        syncNowPollWorkflowIdRef.current = "";
        patchWorkflowSyncNow(workflowId, {
          state: "failed",
          error: message,
        });
      }
    }, 4000);
  };

  /**
   * Persist connector settings from the latest formStateRef values.
   * Skips no-op saves. Used by auto-save after name or filter edits.
   *
   * @returns {Promise<boolean>} True when save completed without validation/API error.
   *
   * @example
   * await persistConnectorSettings();
   * // true
   */
  const persistConnectorSettings = useCallback(async () => {
    const workflow =
      workflowsRef.current.find((w) => String(w.workflow_id || "") === selectedId) || null;
    if (!workflow) return false;

    const {
      formName: latestName,
      formSenderPatterns: latestSenders,
      formSubjectKeywords: latestKeywords,
      dirty: latestDirty,
    } = formStateRef.current;

    const workflowId = String(workflow.workflow_id || "");
    const workflowType = String(workflow.workflow_type || "");
    const trimmedName = String(latestName || "").trim();
    if (!trimmedName) {
      if (mountedRef.current) setSaveError("Name is required");
      return false;
    }

    const nameChanged = trimmedName !== String(workflow.name || "").trim();
    if (!nameChanged && !latestDirty) {
      return true;
    }

    if (mountedRef.current) {
      setSaving(true);
      setSaveError("");
      setSaveSuccess(false);
    }

    try {
      let workflowUpdateResponse = null;
      if (workflowType === "email_daily_digest") {
        if (nameChanged) {
          workflowUpdateResponse = await updateBusinessWorkflow(workflowId, {
            name: trimmedName,
          });
        }
        if (latestDirty) {
          await updateGmailSettings({
            filters: buildSettingsGmailFiltersPayload({
              includeInviteEmails: true,
              meetingSenderPatterns: latestSenders,
              meetingSubjectKeywords: latestKeywords,
            }),
            schedule_timezone: getBrowserTimezone(),
          });
        }
      } else if (nameChanged) {
        workflowUpdateResponse = await updateBusinessWorkflow(workflowId, {
          name: trimmedName,
        });
      }

      const savedWorkflowPatch = {
        ...(workflowUpdateResponse && typeof workflowUpdateResponse === "object"
          ? workflowUpdateResponse
          : {}),
        name: trimmedName,
      };
      const onChange = onWorkflowsChangeRef.current;
      if (typeof onChange === "function") {
        onChange(mergeWorkflowIntoList(workflowsRef.current, workflowId, savedWorkflowPatch));
      }

      if (mountedRef.current) {
        setDirty(false);
        setSaveSuccess(true);
        if (saveSuccessTimerRef.current) clearTimeout(saveSuccessTimerRef.current);
        saveSuccessTimerRef.current = setTimeout(() => {
          if (mountedRef.current) setSaveSuccess(false);
        }, 2000);
      }
      return true;
    } catch (err) {
      if (mountedRef.current) {
        setSaveError(String(err?.message || "Failed to save"));
      }
      return false;
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [selectedId]);

  /**
   * Auto-save with in-flight coalescing so rapid edits do not race.
   *
   * @example
   * await runAutoSave();
   */
  const runAutoSave = useCallback(async () => {
    if (saveInFlightRef.current) {
      saveAgainRef.current = true;
      return;
    }
    saveInFlightRef.current = true;
    try {
      do {
        saveAgainRef.current = false;
        await persistConnectorSettings();
      } while (saveAgainRef.current);
    } finally {
      saveInFlightRef.current = false;
    }
  }, [persistConnectorSettings]);

  /**
   * Start inline name editing from the detail header.
   *
   * @example
   * beginNameEdit();
   */
  const beginNameEdit = () => {
    nameEditActiveRef.current = true;
    setNameDraft(formName);
    setEditingName(true);
  };

  /**
   * Cancel inline name editing without saving.
   *
   * @example
   * cancelNameEdit();
   */
  const cancelNameEdit = () => {
    nameEditActiveRef.current = false;
    setEditingName(false);
    setNameDraft("");
  };

  /**
   * Commit inline name edit and auto-save when the trimmed name changed.
   *
   * @example
   * commitNameEdit();
   */
  const commitNameEdit = () => {
    if (!nameEditActiveRef.current) return;
    nameEditActiveRef.current = false;
    const nextName = String(nameDraft || "").trim();
    const previousName = String(formName || "").trim();
    setEditingName(false);
    if (!nextName) {
      setSaveError("Name is required");
      setNameDraft("");
      return;
    }
    setNameDraft("");
    if (nextName === previousName) return;
    setFormName(nextName);
    formStateRef.current = { ...formStateRef.current, formName: nextName };
    setSaveSuccess(false);
    void runAutoSave();
  };

  /**
   * Update Gmail filter chips and schedule a debounced auto-save.
   *
   * @param {Function} setter - React state setter for the filter field.
   * @param {string} key - formStateRef key ("formSenderPatterns" | "formSubjectKeywords").
   * @returns {(value: string) => void}
   *
   * @example
   * onSenderPatternsChange={scheduleGmailFilterChange(setFormSenderPatterns, "formSenderPatterns")}
   */
  const scheduleGmailFilterChange = (setter, key) => (value) => {
    setter(value);
    formStateRef.current = { ...formStateRef.current, [key]: value, dirty: true };
    setDirty(true);
    setSaveSuccess(false);
    if (gmailAutoSaveTimerRef.current) clearTimeout(gmailAutoSaveTimerRef.current);
    gmailAutoSaveTimerRef.current = setTimeout(() => {
      void runAutoSave();
    }, 500);
  };

  const handleDelete = async () => {
    if (!selectedWorkflow || deleting) return;
    const catalog = CATALOG_BY_WORKFLOW_TYPE[String(selectedWorkflow.workflow_type || "")];
    const displayName = String(selectedWorkflow.name || catalog?.appName || "this connector");
    if (!window.confirm(`Remove "${displayName}"?`)) return;

    const workflowId = String(selectedWorkflow.workflow_id || "").trim();
    if (!workflowId) return;

    setDeleting(true);
    setDeletingWorkflowId(workflowId);
    setSaveError("");
    setSaveSuccess(false);
    try {
      const deleteResult = await deleteBusinessWorkflow(workflowId);
      if (deleteResult?.warning) {
        // Removal itself succeeded; some related cleanup lagged server-side.
        // Not worth blocking/erroring the remove over, but shouldn't be silent.
        console.warn(`Connector ${workflowId} removed with warning:`, deleteResult.warning);
      }
      const nextWorkflows = removeWorkflowFromList(workflows, workflowId);
      if (typeof onWorkflowsChange === "function") {
        onWorkflowsChange(nextWorkflows);
      }
      if (actionItemsSidebar?.setWorkflows) {
        actionItemsSidebar.setWorkflows((previous) =>
          removeWorkflowFromList(previous, workflowId)
        );
      }
      void actionItemsSidebar?.refreshActionItemsData?.({ silent: true });
      if (mountedRef.current) {
        detailLoadedForIdRef.current = null;
        // Only clear selection if the user is still on the removed connector.
        if (String(selectedIdRef.current || "").trim() === workflowId) {
          setSelectedId(null);
          if (isExtension || useCatalogLayout) setShowDetail(false);
        }
        if (String(syncNowPollWorkflowIdRef.current || "").trim() === workflowId) {
          if (syncNowPollRef.current) clearInterval(syncNowPollRef.current);
          syncNowPollWorkflowIdRef.current = "";
        }
        setSyncNowByWorkflowId((previous) => {
          if (!previous[workflowId]) return previous;
          const next = { ...previous };
          delete next[workflowId];
          return next;
        });
      }
    } catch (err) {
      if (mountedRef.current) {
        // Don't paint the failure onto a different connector if the user navigated away.
        const stillViewingRemoved =
          String(selectedIdRef.current || "").trim() === workflowId;
        if (stillViewingRemoved) {
          setSaveError(String(err?.message || "Failed to remove connector"));
        }
      }
    } finally {
      if (mountedRef.current) {
        setDeleting(false);
        setDeletingWorkflowId("");
      }
    }
  };

  // Google Meet "Use Gemini notes": on only when the user wants it AND Drive access exists.
  const isMeetWorkflow = String(selectedWorkflow?.source || "").toLowerCase() === "google_meet";
  const geminiNotesNeedsAccess = selectedWorkflow?.can_offer_notes_access === true;
  const geminiNotesOn = selectedWorkflow?.use_gemini_notes !== false && !geminiNotesNeedsAccess;
  const [geminiNotesError, setGeminiNotesError] = useState("");

  const handleToggleGeminiNotes = async (next) => {
    if (!selectedWorkflow) return;
    const workflowId = resolveWorkflowConnectorId(selectedWorkflow);
    setGeminiNotesError("");
    try {
      // Save the choice first: turning it on without access also sends the user to Google's
      // permission screen (the reconnect), and the choice must survive that round trip.
      const response = await updateBusinessWorkflow(workflowId, { use_gemini_notes: next });
      const onChange = onWorkflowsChangeRef.current;
      if (typeof onChange === "function") {
        onChange(
          mergeWorkflowIntoList(workflowsRef.current, workflowId, {
            ...(response && typeof response === "object" ? response : {}),
            use_gemini_notes: next,
          })
        );
      }
      if (next && geminiNotesNeedsAccess) handleReconnect();
    } catch (error) {
      setGeminiNotesError(String(error?.message || "Could not save this setting. Try again."));
    }
  };

  const handleReconnect = () => {
    if (!selectedWorkflow) return;
    const catalog =
      CATALOG_BY_WORKFLOW_TYPE[String(selectedWorkflow.workflow_type || "")] || null;
    if (!catalog) return;
    const workflowId = resolveWorkflowConnectorId(selectedWorkflow);
    const credentialId = String(selectedWorkflow.credential_id || "").trim();
    const reconnectPath = buildReconnectSetupPath(
      catalog.setupPath,
      workflowId,
      credentialId
    );
    if (isExtension && typeof onStartSetup === "function") {
      onStartSetup(catalog.workflowType, catalog.appName, {
        reconnectWorkflowId: workflowId,
        credentialId,
      });
      return;
    }
    if (typeof onNavigateToSetup === "function") {
      onNavigateToSetup(reconnectPath);
    }
  };

  const handleAddSource = (app) => {
    setAddPickerOpen(false);
    // Attio (and other CRM write-back apps) open a setup panel via setupPath,
    // same chrome as Zoom/Granola — OAuth starts from that panel.
    if (isExtension && typeof onStartSetup === "function" && app?.workflowType) {
      onStartSetup(app.workflowType, app.appName);
      return;
    }
    if (typeof onNavigateToSetup === "function" && app?.setupPath) {
      onNavigateToSetup(app.setupPath);
    }
  };

  const handleRequestSource = (app) => {
    setAddPickerOpen(false);
    setAddPickerSearch("");
    setConnectorRequestTarget(app);
  };

  const closeConnectorRequest = () => {
    setConnectorRequestTarget(null);
  };

  const isAppConnected = useCallback(
    (app) => connectedTypeSet.has(String(app?.workflowType || "")),
    [connectedTypeSet]
  );

  const popularApps = useMemo(
    () => CONNECT_SOURCES_LIVE.filter((app) => POPULAR_CONNECTOR_IDS.includes(app.id)),
    []
  );

  const catalogTableRows = useMemo(
    () =>
      buildCatalogTableRows({
        workflows,
        connectorFilter,
        connectedTypeSet,
        connectedCrmIds: connectedCrmIdSet,
      }),
    [workflows, connectorFilter, connectedTypeSet, connectedCrmIdSet]
  );

  const catalogSearchText = String(catalogSearchQuery || "").trim().toLowerCase();
  const filteredCatalogTableRows = useMemo(() => {
    if (!catalogSearchText) return catalogTableRows;
    return catalogTableRows.filter((row) => {
      const displayName = getCatalogRowDisplayName(row).toLowerCase();
      const appName = String(row.app?.appName || "").toLowerCase();
      return displayName.includes(catalogSearchText) || appName.includes(catalogSearchText);
    });
  }, [catalogTableRows, catalogSearchText]);

  const handleCatalogTableRowClick = (row) => {
    if (row.kind === "coming-soon") {
      handleRequestSource(row.app);
      return;
    }
    if (row.kind === "connected") {
      selectWorkflow(String(row.workflow.workflow_id || ""));
      return;
    }
    if (row.kind === "crm-connected") {
      selectCrmConnector(String(row.app?.id || ""));
      return;
    }
    handleAddSource(row.app);
  };

  const handleCatalogTableConnect = (event, row) => {
    event.stopPropagation();
    if (row.kind === "coming-soon") {
      handleRequestSource(row.app);
      return;
    }
    if (row.kind === "connected") {
      selectWorkflow(String(row.workflow.workflow_id || ""));
      return;
    }
    if (row.kind === "crm-connected") {
      selectCrmConnector(String(row.app?.id || ""));
      return;
    }
    handleAddSource(row.app);
  };

  const addPickerQuery = String(addPickerSearch || "").trim().toLowerCase();
  const filteredAddPickerLive = useMemo(() => {
    if (!addPickerQuery) return CONNECT_SOURCES_LIVE;
    return CONNECT_SOURCES_LIVE.filter((app) =>
      String(app.appName || "").toLowerCase().includes(addPickerQuery)
    );
  }, [addPickerQuery]);
  const filteredAddPickerSoon = useMemo(() => {
    if (!addPickerQuery) return CONNECT_SOURCES_COMING_SOON;
    return CONNECT_SOURCES_COMING_SOON.filter((app) =>
      String(app.appName || "").toLowerCase().includes(addPickerQuery)
    );
  }, [addPickerQuery]);
  const filteredAddPickerCount =
    filteredAddPickerLive.length + filteredAddPickerSoon.length;

  useLayoutEffect(() => {
    if (!addPickerOpen) {
      setAddPickerPortalLayout(null);
      return undefined;
    }

    const syncPickerLayout = () => {
      const anchor = addPickerWrapRef.current;
      if (!anchor) return;
      setAddPickerPortalLayout(
        resolveAddPickerPortalLayout(
          anchor.getBoundingClientRect(),
          window.innerWidth,
          window.innerHeight
        )
      );
    };

    syncPickerLayout();
    window.addEventListener("resize", syncPickerLayout);
    window.addEventListener("scroll", syncPickerLayout, true);
    return () => {
      window.removeEventListener("resize", syncPickerLayout);
      window.removeEventListener("scroll", syncPickerLayout, true);
    };
  }, [addPickerOpen, filteredAddPickerCount]);

  const addPickerListStyle = addPickerPortalLayout
    ? { maxHeight: addPickerPortalLayout.listMaxHeight }
    : undefined;

  const addPicker =
    addPickerOpen && addPickerPortalLayout ? (
      <div
        ref={addPickerRef}
        className={`ds-add-picker ds-add-picker--portal${
          addPickerPortalLayout.openUp ? " ds-add-picker--portal-up" : ""
        }`}
        style={addPickerPortalLayout.style}
      >
        <div className="ds-add-picker__search-wrap">
          <input
            type="search"
            className="ds-add-picker__search"
            value={addPickerSearch}
            onChange={(event) => setAddPickerSearch(event.target.value)}
            placeholder="Search apps…"
            aria-label="Search apps to add"
            autoFocus
          />
        </div>
        {filteredAddPickerCount === 0 ? (
          <p className="ds-add-picker__empty">No apps match your search.</p>
        ) : (
          <ul className="ds-add-picker__list" style={addPickerListStyle}>
            {filteredAddPickerLive.length > 0 ? (
              <li className="ds-add-picker__section-label" aria-hidden="true">
                Available now
              </li>
            ) : null}
            {filteredAddPickerLive.map((app) => {
              const hasAccount = isAppConnected(app);
              return (
                <li key={app.id}>
                  <button
                    type="button"
                    className="ds-add-picker__item"
                    onClick={() => handleAddSource(app)}
                  >
                    <div
                      className={`ds-add-picker__icon business-catalog-app-icon ${app.iconClass}`}
                      aria-hidden="true"
                    >
                      <ConnectSourceCatalogIcon source={app} />
                    </div>
                    <span className="ds-add-picker__text">
                      <span className="ds-add-picker__name">{app.appName}</span>
                      {hasAccount ? (
                        <span className="ds-add-picker__hint">Add account</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
            {filteredAddPickerSoon.length > 0 ? (
              <li className="ds-add-picker__section-label" aria-hidden="true">
                More sources
              </li>
            ) : null}
            {filteredAddPickerSoon.map((app) => (
              <li key={app.id} className="ds-add-picker__request-row">
                <div className="ds-add-picker__soon-label">
                  <div
                    className={`ds-add-picker__icon business-catalog-app-icon ${app.iconClass || ""}`}
                    aria-hidden="true"
                  >
                    <ConnectSourceCatalogIcon source={app} />
                  </div>
                  <span className="ds-add-picker__name">{app.appName}</span>
                </div>
                <button
                  type="button"
                  className="ds-add-picker__request-link"
                  onClick={() => handleRequestSource(app)}
                >
                  Request
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    ) : null;

  const addPickerPortal =
    addPicker && typeof document !== "undefined"
      ? createPortal(addPicker, document.body)
      : null;

  const emptyState = (
    <div className="ds-empty">
      <ConnectSourcesEmptyState
        variant={isExtension ? "extension" : "web"}
        connectedWorkflowTypes={connectedWorkflowTypes}
        onConnectApp={handleAddSource}
        onRequestSource={handleRequestSource}
        showTrustCard={false}
        onGoToTasks={typeof onGoToTasks === "function" ? onGoToTasks : undefined}
        escapeAction={
          typeof onPasteTranscriptInstead === "function"
            ? {
                label: "Not ready? Add tasks manually instead",
                onClick: onPasteTranscriptInstead,
              }
            : null
        }
      />
    </div>
  );

  const catalogSearchControl = (
    <ActionItemsExpandableSearch
      value={catalogSearchQuery}
      onChange={setCatalogSearchQuery}
      panelId="data-sources-search-panel"
      placeholder="Search connectors…"
      ariaLabel="Search connectors"
    />
  );

  const catalog = (
    <div className="ds-catalog">
      <div className="ds-catalog__toolbar">
        <div className="ds-catalog__filters" role="tablist" aria-label="Filter connectors">
          {CONNECTOR_FILTER_TABS.map((tab) => {
            const isActive = connectorFilter === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`ds-catalog__filter${isActive ? " ds-catalog__filter--active" : ""}`}
                onClick={() => setConnectorFilter(tab.id)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        {catalogSearchControl}
      </div>

      {connectorFilter === CONNECTOR_FILTER_ALL ? (
        <section className="ds-catalog__popular" aria-label="Popular connectors">
          <h3 className="ds-catalog__section-title">Popular</h3>
          <div className="ds-catalog__popular-grid">
            {popularApps.map((app) => (
                <div key={app.id} className="ds-catalog__popular-card">
                  <div className="ds-catalog__popular-info">
                    <div
                      className={`ds-catalog__icon business-catalog-app-icon ${app.iconClass || ""}`}
                      aria-hidden="true"
                    >
                      <ConnectSourceCatalogIcon source={app} />
                    </div>
                    <span className="ds-catalog__popular-name">{app.appName}</span>
                  </div>
                  <button
                    type="button"
                    className="ds-catalog__connect-btn"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleAddSource(app);
                    }}
                  >
                    Connect
                  </button>
                </div>
              ))}
          </div>
        </section>
      ) : null}

      <section className="ds-catalog__table-section" aria-label="All connectors">
        <div className="ds-catalog__table-head">
          <span>Connector</span>
          <span>Sync</span>
          <span>Status</span>
        </div>
        {workflowsLoading ? (
          <div className="ds-catalog__loading">
            <InlineSpinner text="Loading connectors…" />
          </div>
        ) : filteredCatalogTableRows.length === 0 ? (
          <p className="ds-catalog__empty">No connectors match this filter.</p>
        ) : (
          <ul className="ds-catalog__table">
            {filteredCatalogTableRows.map((row) => {
              const { app, kind, rowKey } = row;
              const isComingSoon = kind === "coming-soon";
              const workflow = kind === "connected" ? row.workflow : null;
              const connected = kind === "connected" || kind === "crm-connected";
              const displayName = getCatalogRowDisplayName(row);
              const rowSyncError = workflow ? resolveWorkflowSyncError(workflow) : "";
              const syncInProgress =
                workflow &&
                isWorkflowSyncInProgress(workflow, { syncNowByWorkflowId });
              return (
                <li key={rowKey}>
                  <div
                    className="ds-catalog__row"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleCatalogTableRowClick(row)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleCatalogTableRowClick(row);
                      }
                    }}
                  >
                    <span className="ds-catalog__row-connector">
                      <span
                        className={`ds-catalog__icon business-catalog-app-icon ${app.iconClass || ""}`}
                        aria-hidden="true"
                      >
                        <ConnectSourceCatalogIcon source={app} />
                      </span>
                      <span className="ds-catalog__row-name">{displayName}</span>
                    </span>
                    <span
                      className={`ds-catalog__row-sync${
                        rowSyncError ? " ds-catalog__row-sync--error" : ""
                      }${syncInProgress ? " ds-catalog__row-sync--active" : ""}`}
                    >
                      {kind === "crm-connected" ? (
                        getCrmSyncDisplayLabel(crmStatusById?.[String(app?.id || "").trim()])
                      ) : (
                        <WorkflowSyncStatusLabel
                          workflow={workflow}
                          syncNowByWorkflowId={syncNowByWorkflowId}
                        />
                      )}
                    </span>
                    <span className="ds-catalog__row-status">
                      {isComingSoon ? (
                        <button
                          type="button"
                          className="ds-catalog__request-btn"
                          onClick={(event) => handleCatalogTableConnect(event, row)}
                        >
                          Request
                        </button>
                      ) : rowSyncError ? (
                        <span
                          className="ds-catalog__status-error"
                          aria-label="Sync failed"
                          title={`${formatConnectorSyncError(rowSyncError)} (Details: ${rowSyncError})`}
                        >
                          <IoWarningOutline aria-hidden="true" />
                        </span>
                      ) : connected ? (
                        syncInProgress ? (
                          <span className="ds-catalog__status-syncing" aria-label="Syncing">
                            <IoSyncOutline
                              className="ds-catalog__status-syncing-icon"
                              size={14}
                              aria-hidden="true"
                            />
                          </span>
                        ) : (
                        <span className="ds-catalog__status-connected" aria-label="Connected">
                          <IoCheckmark aria-hidden="true" />
                        </span>
                        )
                      ) : (
                        <button
                          type="button"
                          className="ds-catalog__connect-btn ds-catalog__connect-btn--inline"
                          onClick={(event) => handleCatalogTableConnect(event, row)}
                        >
                          Connect
                        </button>
                      )}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );

  // ── Sidebar ────────────────────────────────────────────────────
  const sidebar = (
    <div className="ds-sidebar">
      <div className="ds-sidebar__header">
        <p className="ds-sidebar__section-label">CONNECTORS</p>
        <div className="ds-sidebar__add-wrap" ref={addPickerWrapRef}>
          <button
            type="button"
            className="ds-sidebar__add-btn"
            aria-expanded={addPickerOpen}
            aria-label={addPickerOpen ? "Close add connector menu" : "Add connector"}
            title={addPickerOpen ? "Close" : "Add connector"}
            onClick={() => setAddPickerOpen((o) => !o)}
          >
            <IoAddOutline aria-hidden="true" />
            <span className="ds-sidebar__add-btn-label">{addPickerOpen ? "Close" : "Add"}</span>
          </button>
          {addPickerPortal}
        </div>
      </div>

      {workflowsLoading ? (
        <div className="ds-sidebar__loading">
          <InlineSpinner text="Loading…" />
        </div>
      ) : workflows.length === 0 ? (
        <p className="ds-sidebar__empty">No connectors yet.</p>
      ) : (
        <ul className="ds-sidebar__list">
          {workflows.map((workflow) => {
            const wId = String(workflow.workflow_id || "");
            const catalog =
              CATALOG_BY_WORKFLOW_TYPE[String(workflow.workflow_type || "")] || null;
            const status = getSourceStatus(workflow);
            const isSelected = selectedId === wId;
            const isSyncingThis =
              isWorkflowSyncInProgress(workflow, { syncNowByWorkflowId });
            return (
              <li key={wId} className="ds-sidebar__item-wrap">
                <button
                  type="button"
                  className={`ds-sidebar__item${isSelected ? " ds-sidebar__item--active" : ""}`}
                  onClick={() => selectWorkflow(wId)}
                >
                  <div
                    className={`ds-sidebar__app-icon business-catalog-app-icon ${catalog?.iconClass || ""}`}
                    aria-hidden="true"
                  >
                    <ConnectSourceCatalogIcon source={catalog} />
                  </div>
                  <div className="ds-sidebar__item-body">
                    <span className="ds-sidebar__item-name">
                      {String(workflow.name || catalog?.appName || "").trim()}
                    </span>
                    <span className="ds-sidebar__item-meta">
                      <WorkflowSyncStatusLabel
                        workflow={workflow}
                        syncNowByWorkflowId={syncNowByWorkflowId}
                      />
                    </span>
                  </div>
                  {deletingWorkflowId === wId ? (
                    <span
                      className="ai-loading-spinner ai-loading-spinner--xs ds-sidebar__item-spinner"
                      role="status"
                      aria-label="Removing connector"
                    />
                  ) : (
                    <span
                      className={`ds-sidebar__dot ds-sidebar__dot--${isSyncingThis ? "syncing" : status.dot}`}
                      aria-label={status.label}
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  // ── Detail pane ─────────────────────────────────────────────────
  const isGmail =
    !!selectedWorkflow &&
    String(selectedWorkflow.workflow_type || "") === "email_daily_digest";
  const isGranola =
    !!selectedWorkflow &&
    String(selectedWorkflow.workflow_type || "") === "granola_daily_digest";
  const isNotion =
    !!selectedWorkflow &&
    String(selectedWorkflow.workflow_type || "") === "notion_daily_digest";
  const selectedCatalog = selectedWorkflow
    ? CATALOG_BY_WORKFLOW_TYPE[String(selectedWorkflow.workflow_type || "")] || null
    : null;
  // `deleting` is process-wide (blocks concurrent removes); the spinner/disable
  // chrome must only apply to the connector actually being removed.
  const isDeletingSelected =
    Boolean(deletingWorkflowId) &&
    deletingWorkflowId === String(selectedWorkflow?.workflow_id || "").trim();
  const needsReconnect =
    !!selectedWorkflow &&
    !isGranola &&
    workflowNeedsReconnect(selectedWorkflow);
  // Persisted failure from the backend (survives navigation/tab-close). In-session
  // Sync now errors are stored per workflow in syncNowByWorkflowId.
  // Reconnect-worthy errors get their own banner below, so skip those here.
  const persistedSyncError =
    selectedWorkflow && !needsReconnect
      ? resolveWorkflowSyncError(selectedWorkflow)
      : "";
  const tasksFilterPath = selectedWorkflowId
    ? buildActionItemsListPath({ workflow: selectedWorkflowId })
    : "";
  const statusNotice = saveError
    ? { tone: "error", text: saveError }
    : selectedSyncNowState === "failed" && selectedSyncNowError
      ? {
          tone: "error",
          text: `Sync failed: ${formatConnectorSyncError(selectedSyncNowError)}`,
          title: `Details: ${selectedSyncNowError}`,
          dismissKey: String(selectedSyncNowError),
        }
      : saving
        ? { tone: "muted", text: "Saving…" }
        : saveSuccess
          ? { tone: "ok", text: "Saved." }
          : persistedSyncError &&
              !isWorkflowSyncActive(selectedSyncNow)
            ? {
                tone: "error",
                text: `Sync failed: ${formatConnectorSyncError(persistedSyncError)}`,
                title: `Details: ${persistedSyncError}`,
                dismissKey: String(persistedSyncError),
              }
            : null;
  // A dismissed sync error stays hidden until its text changes or the next Sync now.
  const syncNoticeDismissed =
    Boolean(statusNotice?.dismissKey) &&
    dismissedSyncErrors[String(selectedWorkflowId || "")] === statusNotice.dismissKey;
  const visibleStatusNotice = syncNoticeDismissed ? null : statusNotice;

  const detail = selectedWorkflow ? (
    <div className="ds-detail">
      <div className="ds-detail__header">
        {useCatalogLayout || !useStackedLayout ? (
          <div
            className={`ds-detail__app-icon business-catalog-app-icon ${selectedCatalog?.iconClass || ""}`}
            aria-hidden="true"
          >
            <ConnectSourceCatalogIcon source={selectedCatalog} />
          </div>
        ) : null}
        <div className="ds-detail__header-text">
          {editingName ? (
            <input
              ref={nameInputRef}
              id="ds-name"
              className="ds-detail__name-input"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={commitNameEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitNameEdit();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelNameEdit();
                }
              }}
              aria-label="Connector name"
              disabled={saving || isDeletingSelected}
            />
          ) : (
            <div className="ds-detail__name-row">
              <h3 className="ds-detail__name">
                {String(formName || selectedCatalog?.appName || "").trim()}
              </h3>
              <button
                type="button"
                className="ds-detail__name-edit"
                onClick={beginNameEdit}
                disabled={saving || isDeletingSelected}
                aria-label="Edit name"
                title="Edit name"
              >
                <IoPencilOutline size={14} aria-hidden="true" />
              </button>
              {tasksFilterPath ? (
                <Link
                  to={tasksFilterPath}
                  className="ds-detail__view-tasks"
                  title="View tasks from this connector"
                >
                  View tasks
                </Link>
              ) : null}
            </div>
          )}
          {selectedWorkflow ? (
            <WorkflowSyncStatusLabel
              workflow={selectedWorkflow}
              syncNowByWorkflowId={syncNowByWorkflowId}
              className="ds-detail__header-meta"
            />
          ) : null}
        </div>
        <div className="ds-detail__header-actions">
          <button
            type="button"
            className={`ds-detail__sync-icon${
              selectedSyncInProgress ? " ds-detail__sync-icon--spinning" : ""
            }`}
            onClick={handleSyncNow}
            disabled={isDeletingSelected || selectedSyncInProgress}
            aria-label="Sync now"
            title="Sync now"
          >
            <IoSyncOutline size={18} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="ds-detail__remove-icon"
            onClick={handleDelete}
            disabled={deleting || saving}
            aria-label={
              isDeletingSelected ? "Removing connector" : "Remove connector"
            }
            title={
              deleting && !isDeletingSelected
                ? "Another connector is being removed"
                : "Remove connector"
            }
          >
            <IoTrashOutline size={17} aria-hidden="true" />
          </button>
        </div>
      </div>

      {visibleStatusNotice && !(deleting && !isDeletingSelected && saveError) ? (
        <div
          className={`ds-detail__status ds-detail__status--${visibleStatusNotice.tone}`}
          aria-live="polite"
          title={visibleStatusNotice.title}
        >
          <span>{visibleStatusNotice.text}</span>
          {visibleStatusNotice.dismissKey ? (
            <button
              type="button"
              className="ds-detail__status-dismiss"
              aria-label="Dismiss sync error"
              title="Dismiss"
              onClick={() =>
                setDismissedSyncErrors((prev) => ({
                  ...prev,
                  [String(selectedWorkflowId || "")]: visibleStatusNotice.dismissKey,
                }))
              }
            >
              ×
            </button>
          ) : null}
        </div>
      ) : null}

      {isDeletingSelected ? (
        <div className="ds-detail__loading">
          <InlineSpinner text="Removing connector…" />
        </div>
      ) : (
        <div className="ds-detail__body">
        <div className="ds-detail__settings">
          {isGmail ? (
            gmailFiltersLoading ? (
              <div className="ds-detail__loading ds-detail__loading--section">
                <InlineSpinner text="Loading Gmail filter details…" />
              </div>
            ) : (
              <div className="ds-email-filters">
                <button
                  type="button"
                  className="ds-email-filters__toggle"
                  aria-expanded={gmailFiltersOpen}
                  onClick={() => setGmailFiltersOpen((open) => !open)}
                >
                  <IoChevronDown
                    className={`ds-email-filters__chevron${
                      gmailFiltersOpen ? " ds-email-filters__chevron--open" : ""
                    }`}
                    aria-hidden="true"
                  />
                  Email filters
                </button>
                {gmailFiltersOpen ? (
                  <div className="ds-email-filters__body">
                    <GmailFilterSetupCard
                      senderPatterns={formSenderPatterns}
                      subjectKeywords={formSubjectKeywords}
                      onSenderPatternsChange={scheduleGmailFilterChange(
                        setFormSenderPatterns,
                        "formSenderPatterns"
                      )}
                      onSubjectKeywordsChange={scheduleGmailFilterChange(
                        setFormSubjectKeywords,
                        "formSubjectKeywords"
                      )}
                    />
                  </div>
                ) : null}
              </div>
            )
          ) : null}

          {isNotion ? (
            <NotionMeetingsPanel workflowId={String(selectedWorkflow.workflow_id || "")} />
          ) : null}

          {needsReconnect &&
          !isWorkflowSyncActive(selectedSyncNow) ? (
            <div className="ds-auth-row ds-auth-row--error">
              <span
                className="ds-auth-row__error"
                title={
                  resolveWorkflowSyncError(selectedWorkflow)
                    ? `Details: ${resolveWorkflowSyncError(selectedWorkflow)}`
                    : undefined
                }
              >
                Connection expired. Click Reconnect to sign in again.
              </span>
              <button
                type="button"
                className="ds-auth-row__reconnect"
                onClick={handleReconnect}
              >
                Reconnect
              </button>
            </div>
          ) : null}

          {/* Google Meet only, and optional: the connector works without it. For calls with
              no transcript, the Meet notes Google generated can be read instead; that needs
              Drive access the user may not have granted. The switch is the user's choice and
              the extractor honours it (connector_metadata.use_gemini_notes), so off means
              off even when access was granted. Always shown for Meet, never hidden after a
              dismissal elsewhere. */}
          {isMeetWorkflow && !needsReconnect ? (
            <div className="ds-auth-row ds-auth-row--offer">
              <div className="ds-auth-row__offer-body">
                <SettingsToggleRow
                  id="ds-gemini-notes-toggle"
                  label="Use Gemini notes"
                  description={
                    geminiNotesNeedsAccess
                      ? "For calls with no transcript, read the notes Google Meet generated and extract your action items from them. Turning this on asks for one-time Google Drive access, only to Meet's own files. Transcripts keep working either way."
                      : "For calls with no transcript, read the notes Google Meet generated and extract your action items from them. Transcripts keep working either way."
                  }
                  pressed={geminiNotesOn}
                  onChange={(next) => void handleToggleGeminiNotes(next)}
                />
                {geminiNotesError ? (
                  <p className="ds-auth-row__hint ds-auth-row__hint--error" role="alert">
                    {geminiNotesError}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        <div className="ds-detail__sync-panel">
          <WorkflowSyncActivityPanel
            syncStats={syncStats}
            loading={syncStatsLoading}
            workflowType={selectedWorkflow?.workflow_type || ""}
          />
        </div>
        </div>
      )}
    </div>
  ) : (
    <div className="ds-detail ds-detail--empty">
      <p className="ds-detail__empty-hint">Select a connector from the list to view its settings.</p>
    </div>
  );

  const crmDetail =
    selectedCrmApp && selectedCrmStatus?.connected ? (
      <CrmConnectorDetailPanel
        app={selectedCrmApp}
        status={selectedCrmStatus}
        onReconnect={() => handleAddSource(selectedCrmApp)}
        onStatusChange={(crmId, nextStatus) => {
          onCrmStatusChange?.(crmId, nextStatus);
          if (!nextStatus?.connected) {
            setSelectedCrmId("");
            setShowDetail(false);
          }
        }}
      />
    ) : null;

  const rootClassName = `ds-root${isExtension ? " ds-root--extension" : " ds-root--web"}${
    useCatalogLayout ? " ds-root--catalog" : ""
  }${useStackedLayout && workflows.length > 0 ? " ds-root--stacked" : ""}`;

  const connectorRequestSource = isExtension
    ? "extension-data-sources"
    : "data-sources-add-picker";
  const connectorRequestModal = (
    <ConnectorRequestModal
      connector={connectorRequestTarget}
      source={connectorRequestSource}
      onClose={closeConnectorRequest}
    />
  );

  // Arrived via a deep link (setup / OAuth return) to a connector the cached list does not
  // contain yet: hold a spinner until the live list lands instead of flashing the catalog
  // (or the empty state) and then swapping to the connector's detail.
  const focusedWorkflowId = String(focusWorkflowId || "").trim();
  const focusedAppId = String(focusAppId || "").trim();
  const focusTargetPresent = focusedWorkflowId
    ? workflows.some((w) => String(w.workflow_id || "") === focusedWorkflowId)
    : APP_ID_TO_WORKFLOW_TYPE[focusedAppId]
      ? connectedTypeSet.has(APP_ID_TO_WORKFLOW_TYPE[focusedAppId])
      : connectedCrmIdSet.has(focusedAppId);
  const awaitingFocusTarget =
    useCatalogLayout &&
    focusPending &&
    Boolean(focusedWorkflowId || focusedAppId) &&
    !focusTargetPresent;

  if (awaitingFocusTarget) {
    return (
      <>
        <div className={`${rootClassName} ds-root--empty`}>
          <div className="ds-empty__loading">
            <InlineSpinner text="Loading connectors…" />
          </div>
        </div>
        {connectorRequestModal}
      </>
    );
  }

  if (useCatalogLayout) {
    return (
      <>
        <div className={rootClassName}>
          {showDetail && selectedWorkflow
            ? detail
            : showDetail && crmDetail
              ? crmDetail
              : catalog}
        </div>
        {connectorRequestModal}
      </>
    );
  }

  if (workflowsLoading) {
    return (
      <>
        <div className={`${rootClassName} ds-root--empty`}>
          <div className="ds-empty__loading">
            <InlineSpinner text="Loading connectors…" />
          </div>
        </div>
        {connectorRequestModal}
      </>
    );
  }

  if (workflows.length === 0) {
    return (
      <>
        <div className={`${rootClassName} ds-root--empty`}>{emptyState}</div>
        {connectorRequestModal}
      </>
    );
  }

  // Extension: stacked (list → detail)
  if (useStackedLayout) {
    return (
      <>
        <div className={rootClassName}>
          {showDetail ? detail : sidebar}
        </div>
        {connectorRequestModal}
      </>
    );
  }

  return null;
});

export default DataSourcesView;
